/**
 * Fields that must invalidate the stabilized auth session.
 *
 * `useStableSession` ignores better-auth reference churn, but a flag the
 * phone/email gates read has to be in this comparison. Omitting
 * `phoneNumberVerified` keeps a successful WhatsApp OTP looking unverified
 * for the rest of the SPA session.
 */

export type StableAuthSession = {
  user?: {
    id?: string;
    email?: string;
    emailVerified?: boolean;
    phone?: string | null;
    image?: string | null;
    phoneNumber?: string | null;
    phoneNumberVerified?: boolean | null;
    banned?: boolean | null;
    role?: string | null;
    permissions?: readonly string[] | null;
  } | null;
  session?: {
    activeOrganizationId?: string | null;
    impersonatedBy?: string | null;
    tenantImpersonatedBy?: string | null;
  } | null;
  member?: { id?: string; role?: string | null } | null;
  hasPendingTerms?: boolean;
  organizationLifecycle?: {
    reason?: string | null;
    status?: string | null;
  } | null;
} | null;

function permissionsSignature(session: StableAuthSession): string {
  const permissions = session?.user?.permissions;
  return Array.isArray(permissions) ? permissions.join(",") : "";
}

function cell(value: unknown): string {
  if (value === null) return "null";
  if (value === undefined) return "undef";
  if (typeof value === "boolean") return value ? "true" : "false";
  return String(value);
}

function sessionChangeKey(session: StableAuthSession): string {
  const user = session?.user;
  const inner = session?.session;
  const lifecycle = session?.organizationLifecycle;
  const parts = [
    cell(user?.id),
    cell(user?.email),
    cell(user?.emailVerified),
    cell(user?.phone),
    cell(user?.image),
    cell(user?.phoneNumber),
    cell(user?.phoneNumberVerified),
    cell(user?.banned),
    cell(user?.role),
    cell(inner?.activeOrganizationId),
    cell(inner?.impersonatedBy),
    cell(inner?.tenantImpersonatedBy),
    cell(session?.member?.id),
    cell(session?.member?.role),
    cell(session?.hasPendingTerms),
    cell(lifecycle?.reason),
    cell(lifecycle?.status),
    permissionsSignature(session),
    session === null ? "null" : "set",
  ];
  return parts.join("\0");
}

export function hasStableSessionChanged(
  current: StableAuthSession,
  next: StableAuthSession,
): boolean {
  return sessionChangeKey(current) !== sessionChangeKey(next);
}
